import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { APIClient } from '../utils/APIClient';
import { isOk } from '../utils/apiResponse';
import { API_URL } from '../settings/local_settings';
import { LocalStorage } from '../utils/LocalStorage';
import './PendingInvitationBanner.scss';

const DISMISS_KEY_PREFIX = 'pending_invitation_dismissed_';

// En estas pantallas el banner sobra (la invitación ya está a la vista) o todavía no hay sesión.
const HIDDEN_PATH_PREFIXES = ['/accept-invitation', '/login', '/signup'];

function wasDismissed(hash) {
    try {
        return sessionStorage.getItem(DISMISS_KEY_PREFIX + hash) === '1';
    } catch (e) {
        return false;
    }
}

function rememberDismissal(hash) {
    try {
        sessionStorage.setItem(DISMISS_KEY_PREFIX + hash, '1');
    } catch (e) {
        // Sin storage el «Ahora no» dura hasta recargar: alcanza.
    }
}

/**
 * «Te invitaron a unirte al equipo de X» para quien ya está adentro de Dashbo.
 *
 * El caso que lo motivó (Not Ordinary Agency, 24/09/2026): al invitado le dicen «entrá a Dashbo»,
 * entra por dashbo.io ANTES de que exista la invitación y el alta le crea una agencia propia.
 * Cuando la invitación llega, nada en la app se la muestra: el login sólo redirige a la invitación
 * si ya existía al entrar, y `Default.js` sólo si el usuario no tiene agencia. El único camino era
 * encontrar el mail y abrir el link.
 *
 * Qué invitación ofrecer lo decide el backend (`invitation/pending-for-me`) con el MISMO criterio
 * que la guarda del accept: si el usuario ya está en una agencia con equipo y plataformas, no hay
 * nada que ofrecer, porque aceptarla daría error.
 *
 * Se pregunta al entrar (una vez por token) y cada vez que el banner vuelve a ser visible después
 * de una pantalla donde se oculta. Una invitación que llega con la app abierta aparece en esa
 * transición o al volver a entrar.
 */
export const PendingInvitationBanner = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [invitation, setInvitation] = useState(null);
    const [checkedToken, setCheckedToken] = useState(null);

    const token = LocalStorage.hasUserData() ? LocalStorage.fetchUserToken() : null;
    const hiddenHere = HIDDEN_PATH_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
    const wasHidden = useRef(hiddenHere);

    const fetchPendingInvitation = () => {
        new APIClient(API_URL + 'invitation/pending-for-me').call('GET').then((response) => {
            // Cualquier cosa que no sea un OK con invitación deja el banner apagado: es una
            // sugerencia, no puede mostrar un error ni tapar la pantalla.
            const found = isOk(response) && response.data ? response.data.invitation : null;
            setInvitation(found && found.hash && !wasDismissed(found.hash) ? found : null);
        });
    };

    useEffect(() => {
        if (!token || token === checkedToken) return;
        setCheckedToken(token);
        fetchPendingInvitation();
    }, [token, checkedToken]);

    // Se vuelve a preguntar cada vez que el banner pasa de oculto a visible: al salir de la
    // invitación (aceptada, o con «atrás»), del login o del alta. El token NO cambia al aceptar
    // (el DRF Token es permanente) y `App` no se remonta, así que sin esto quien acaba de aceptar
    // volvía a ver «Te invitaron a unirte a X» (C-INVITE-01).
    //
    // Va sobre la TRANSICIÓN de la ruta y no sobre una bandera armada en el click de «Unirme»:
    // react-router aplica la navegación después del render del click, así que en ese render la
    // ruta todavía es la de origen y la consulta salía antes de aceptar nada (QA-invit-03, 26/09).
    //
    // `checkedToken === token` evita la consulta doble al entrar desde el login: esa la hace el
    // efecto de arriba, con el token nuevo.
    useEffect(() => {
        if (wasHidden.current && !hiddenHere && token && checkedToken === token) {
            fetchPendingInvitation();
        }
        wasHidden.current = hiddenHere;
    }, [hiddenHere, token, checkedToken]);

    if (!token || !invitation || hiddenHere) return null;

    const handleJoin = () => {
        const hash = invitation.hash;
        setInvitation(null);
        navigate('/accept-invitation/' + hash);
    };

    const handleDismiss = () => {
        rememberDismissal(invitation.hash);
        setInvitation(null);
    };

    return (
        <div className="pending-invitation-banner" data-testid="pending-invitation-banner" role="status">
            <div className="pending-invitation-banner-content">
                <span className="pending-invitation-banner-text">
                    {t('pending_invitation_banner.message', {
                        agency: invitation.agency_name,
                        interpolation: { escapeValue: false },
                    })}
                </span>
                <button className="pending-invitation-banner-cta" onClick={handleJoin}>
                    {t('pending_invitation_banner.cta')}
                </button>
                <button className="pending-invitation-banner-dismiss" onClick={handleDismiss}>
                    {t('pending_invitation_banner.dismiss')}
                </button>
            </div>
        </div>
    );
};
